import React, { PropsWithChildren, useEffect, useMemo, useState, useCallback } from 'react';
import DossierPrescriberContext from './DossierPrescriberContext';
import useDossierPrescripteur, { Tiers } from '../hooks/useDossierPrescripteur';

const DossierPrescriberProvider: React.FunctionComponent<PropsWithChildren> = function ({
    children,
}) {
    const { getPrescriber, getSousClients } = useDossierPrescripteur();
    const [prescriberId, setPrescriberId] = useState<number | undefined>();
    const [prescriber, setPrescriber] = useState<Tiers | undefined>();
    const [prescriberLoaded, setPrescriberLoaded] = useState<boolean>(false);
    const [sousClients, setSousClients] = useState<Tiers[]>([]);

    const loadPrescriber = useCallback(
        async (newPrescriberId: number) => {
            setPrescriberLoaded(false);
            getPrescriber(newPrescriberId)
                .then((result) => {
                    setPrescriberId(result.id);
                    setPrescriber(result as unknown as Tiers);
                })
                .catch(() => {
                    setPrescriberId(undefined);
                    setPrescriber(undefined);
                })
                .finally(() => setPrescriberLoaded(true));

            getSousClients(newPrescriberId)
                .then((result) => {
                    setSousClients(result);
                })
                .catch(() => {
                    setSousClients([]);
                });
        },
        [getPrescriber, getSousClients],
    );

    useEffect(() => {
        if (prescriberId) {
            if (!prescriber || prescriberId !== prescriber.id) {
                loadPrescriber(prescriberId);
            }
        } else {
            setPrescriber(undefined);
        }
    }, [prescriberId, loadPrescriber, prescriber]);

    const contextValue = useMemo(
        () => ({
            prescriberId,
            setPrescriberId,
            prescriber,
            setPrescriber,
            sousClients,
            prescriberLoaded,
        }),
        [prescriberId, setPrescriberId, prescriber, setPrescriber, sousClients, prescriberLoaded],
    );

    return (
        <DossierPrescriberContext.Provider value={contextValue}>
            {children}
        </DossierPrescriberContext.Provider>
    );
};

export default DossierPrescriberProvider;
